import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Paper, Divider, CircularProgress } from "@mui/material";

//
// TABLE HELPER COMPONENTS
//

const Row = ({ label, value }) => (
  <tr>
    <td style={{ width: "30%", padding: "6px", border: "1px solid #ddd", fontWeight: 600, wordBreak: "break-word" }}>
      {label}
    </td>
    <td style={{ width: "70%", padding: "6px", border: "1px solid #ddd", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {value ?? "—"}
    </td>
  </tr>
);

const Section = ({ title }) => (
  <tr>
    <td colSpan={2} style={{ padding: "8px", border: "1px solid #ddd", fontWeight: "bold" }}>
      {title}
    </td>
  </tr>
);

const CheckRow = ({ checked, label, detail }) => (
  <tr>
    <td style={{ width: "30%", padding: "6px", border: "1px solid #ddd", fontWeight: 600, wordBreak: "break-word" }}>
      <span style={{ marginRight: 8 }}>{checked ? "☑" : "☐"}</span>
      {label}
    </td>
    <td style={{ width: "70%", padding: "6px", border: "1px solid #ddd", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {checked ? (String(detail || "").trim() ? detail : "—") : ""}
    </td>
  </tr>
);

//
// UTILITY FUNCTIONS
//

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const fmtTime = (val) => {
  if (!val) return "—";
  const raw = String(val).trim();

  const ampmAlready = raw.match(/^\s*\d{1,2}(:\d{2})?\s*([AaPp][Mm])\s*$/);
  if (ampmAlready) {
    const [, , ap] = ampmAlready;
    return raw.replace(/([AaPp][Mm])/, ap.toUpperCase()).replace(/\s+/g, " ").trim();
  }

  const m24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m24) {
    let h = parseInt(m24[1], 10);
    const min = m24[2];
    if (Number.isNaN(h)) return "—";
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${min} ${ampm}`;
  }

  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    let h = d.getHours();
    const min = String(d.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${min} ${ampm}`;
  }

  return raw;
};

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v === 0) return 0;
    if (v !== null && v !== undefined) {
      const s = String(v);
      if (s.trim() !== "") return v;
    }
  }
  return "";
};

const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") return ["true", "yes", "y", "1"].includes(v.trim().toLowerCase());
  return !!v;
};

const parseJsonDeep = (input, maxDepth = 5) => {
  let v = input;
  let depth = 0;
  try {
    while (typeof v === "string" && depth < maxDepth) {
      v = JSON.parse(v);
      depth++;
    }
  } catch {
    // Stop on parse errors
  }
  return v;
};

const asArray = (val) => {
  const v = parseJsonDeep(val);
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object") return [v];
  return [];
};

// 
// MAIN COMPONENT
// 

const PrenatalVisitPreview = ({ data }) => {
  const [form, setForm] = useState({});
  const [pn, setPn] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { patientFormId, formTitle, particeName } = data || {};

  useEffect(() => {
    if (!patientFormId) {
      setLoading(false);
      setError("No Patient Form ID provided");
      return;
    }

    let active = true;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("neuro_patientportal_auth_token");

        if (!token) {
          throw new Error("Authentication token not found. Please log in.");
        }

        const headers = { Authorization: `Bearer ${token}` };

        const formUrl = `${process.env.REACT_APP_API_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`;
        const prenatalUrl = `${process.env.REACT_APP_API_URL}/DoulaForms/getPrenatalVisitById?PatientFormId=${patientFormId}`;

        const [formResponse, prenatalResponse] = await Promise.all([
          fetch(formUrl, { headers }),
          fetch(prenatalUrl, { headers })
        ]);

        if (!active) return;

        if (!formResponse.ok) {
          throw new Error(`Form load failed (${formResponse.status})`);
        }
        const formRaw = await formResponse.json();
        const formData = Array.isArray(formRaw) ? (formRaw[0] || {}) : (formRaw || {});

        let prenatalData = {};
        if (prenatalResponse.ok) {
          const prenatalText = await prenatalResponse.text();
          const parsed = parseJsonDeep(prenatalText);
          const arr = asArray(parsed);
          const firstObj = arr[0] || {};

          prenatalData = (firstObj.data && typeof firstObj.data === "object")
            ? firstObj.data
            : firstObj;
        }

        if (active) {
          setForm(formData);
          setPn(prenatalData);
        }

      } catch (err) {
        if (active) {
          console.error("Error fetching data:", err);
          setError(err.message || "Failed to load Prenatal Visit.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [patientFormId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: "60vh", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  // PREPARE DATA
  const headerTitle = firstNonEmpty(formTitle) || "Prenatal Visit";
  const dosVal = fmtDate(firstNonEmpty(pn.DOS, pn.dos, form.assessmentDate));
  const fileNo = firstNonEmpty(form.accountNo) || "—";
  const clientName = firstNonEmpty(form.patientName) || "—";
  const dobVal = fmtDate(firstNonEmpty(form.dob, form.dateOfBirth));

  const timeIn = firstNonEmpty(pn.timeIn, pn.TimeIn);
  const timeOut = firstNonEmpty(pn.timeOut, pn.TimeOut);
  const location = firstNonEmpty(pn.location, pn.Location);

  const newDiagnosis = firstNonEmpty(pn.newDiagnosis, pn.NewDiagnosis);
  const newMedication = firstNonEmpty(pn.newMedication, pn.NewMedication);
  const changeInHousingFinances = firstNonEmpty(pn.changeInHousingFinances, pn.ChangeInHousingFinances);
  const overallHealth = firstNonEmpty(pn.overallHealth, pn.OverallHealth);
  const updateFromLastVisit = firstNonEmpty(pn.updateFromLastVisit, pn.UpdateFromLastVisit);

  const calmBreathPregnancy = firstNonEmpty(pn.calmBreathPregnancy, pn.CalmBreathPregnancy);
  const birthingTimeBreath = firstNonEmpty(pn.birthingTimeBreath, pn.BirthingTimeBreath);
  const visualization = firstNonEmpty(pn.visualization, pn.Visualization);

  const classesCompletedPlanned = toBool(pn.classesCompletedPlanned);
  const classesCompletedPlannedDetail = firstNonEmpty(pn.classesCompletedPlannedDetail);
  const questionsAnswered = toBool(pn.questionsAnswered);
  const questionsAnsweredDetail = firstNonEmpty(pn.questionsAnsweredDetail);
  const reviewStagesOfLabor = toBool(pn.reviewStagesOfLabor);
  const reviewStagesOfLaborDetail = firstNonEmpty(pn.reviewStagesOfLaborDetail);
  const cervicalExams = toBool(pn.cervicalExams);
  const cervicalExamsDetail = firstNonEmpty(pn.cervicalExamsDetail);
  const acupressurePointsHandout = toBool(pn.acupressurePointsHandout);
  const acupressurePointsHandoutDetail = firstNonEmpty(pn.acupressurePointsHandoutDetail);

  const reviewActivityLevel = toBool(pn.reviewActivityLevel);
  const reviewActivityLevelDetail = firstNonEmpty(pn.reviewActivityLevelDetail);
  const birthBall = toBool(pn.birthBall);
  const birthBallDetail = firstNonEmpty(pn.birthBallDetail);
  const movementTechniques = toBool(pn.movementTechniques);
  const movementTechniquesDetail = firstNonEmpty(pn.movementTechniquesDetail);
  const withBirthPartner = toBool(pn.withBirthPartner);
  const withBirthPartnerDetail = firstNonEmpty(pn.withBirthPartnerDetail);

  const choiceCardActivity = toBool(pn.choiceCardActivity);
  const choiceCardActivityDetail = firstNonEmpty(pn.choiceCardActivityDetail);
  const birthLetterDraft = toBool(pn.birthLetterDraft);
  const birthLetterDraftDetail = firstNonEmpty(pn.birthLetterDraftDetail);
  const cervicalExamsInfo = toBool(pn.cervicalExamsInfo);
  const cervicalExamsInfoDetail = firstNonEmpty(pn.cervicalExamsInfoDetail);
  const bodyAutonomy = toBool(pn.bodyAutonomy);
  const bodyAutonomyDetail = firstNonEmpty(pn.bodyAutonomyDetail);
  const packingListSnack = toBool(pn.packingListSnack);
  const packingListSnackDetail = firstNonEmpty(pn.packingListSnackDetail);

  const whoWillAttend = toBool(pn.whoWillAttend);
  const whoWillAttendDetail = firstNonEmpty(pn.whoWillAttendDetail);
  const doulaExpectations = toBool(pn.doulaExpectations);
  const doulaExpectationsDetail = firstNonEmpty(pn.doulaExpectationsDetail);
  const medicalStaffExpectations = toBool(pn.medicalStaffExpectations);
  const medicalStaffExpectationsDetail = firstNonEmpty(pn.medicalStaffExpectationsDetail);
  const partnerExpectations = toBool(pn.partnerExpectations);
  const partnerExpectationsDetail = firstNonEmpty(pn.partnerExpectationsDetail);
  const surahsDuringLabor = toBool(pn.surahsDuringLabor);
  const surahsDuringLaborDetail = firstNonEmpty(pn.surahsDuringLaborDetail);
  const physicalModesty = toBool(pn.physicalModesty);
  const physicalModestyDetail = firstNonEmpty(pn.physicalModestyDetail);

  const postpartumPlanningSheet = toBool(pn.postpartumPlanningSheet);
  const postpartumPlanningSheetDetail = firstNonEmpty(pn.postpartumPlanningSheetDetail);
  const mainSupportPeople = toBool(pn.mainSupportPeople);
  const mainSupportPeopleDetail = firstNonEmpty(pn.mainSupportPeopleDetail);
  const safeSleepHandout = toBool(pn.safeSleepHandout);
  const safeSleepHandoutDetail = firstNonEmpty(pn.safeSleepHandoutDetail);
  const babyWearing = toBool(pn.babyWearing);
  const babyWearingDetail = firstNonEmpty(pn.babyWearingDetail);
  const returnToActivity = toBool(pn.returnToActivity);
  const returnToActivityDetail = firstNonEmpty(pn.returnToActivityDetail);
  const returnToSex = toBool(pn.returnToSex);
  const returnToSexDetail = firstNonEmpty(pn.returnToSexDetail);

  const lactationClasses = toBool(pn.lactationClasses);
  const lactationClassesDetail = firstNonEmpty(pn.lactationClassesDetail);
  const feedingPlanCompleted = toBool(pn.feedingPlanCompleted);
  const feedingPlanCompletedDetail = firstNonEmpty(pn.feedingPlanCompletedDetail);
  const breastChanges = firstNonEmpty(pn.breastChanges);
  const firstFiveDaysPostpartum = toBool(pn.firstFiveDaysPostpartum);
  const firstFiveDaysPostpartumDetail = firstNonEmpty(pn.firstFiveDaysPostpartumDetail);
  const firstTwoWeeksPostpartum = toBool(pn.firstTwoWeeksPostpartum);
  const firstTwoWeeksPostpartumDetail = firstNonEmpty(pn.firstTwoWeeksPostpartumDetail);
  const breastPumpsTools = toBool(pn.breastPumpsTools);
  const breastPumpsToolsDetail = firstNonEmpty(pn.breastPumpsToolsDetail);
  const alcoholBreastfeeding = toBool(pn.alcoholBreastfeeding);
  const alcoholBreastfeedingDetail = firstNonEmpty(pn.alcoholBreastfeedingDetail);
  const medicationsBreastfeeding = toBool(pn.medicationsBreastfeeding);
  const medicationsBreastfeedingDetail = firstNonEmpty(pn.medicationsBreastfeedingDetail);

  const foodYesterday = firstNonEmpty(pn.foodYesterday);
  const waterIntake = firstNonEmpty(pn.waterIntake);
  const constipation = firstNonEmpty(pn.constipation);
  const mealPrepping = firstNonEmpty(pn.mealPrepping);
  const mentalHealthHistory = firstNonEmpty(pn.mentalHealthHistory);
  const anxietyDepression = firstNonEmpty(pn.anxietyDepression);
  const frequentWorrying = firstNonEmpty(pn.frequentWorrying);
  const laborBirthWorries = firstNonEmpty(pn.laborBirthWorries);

  return (
    <Paper sx={{ p: 0, borderRadius: 2, overflow: "hidden" }}>
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
          <strong>DOS:</strong> {dosVal}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
          }}
        >
          {headerTitle}
        </Typography>

        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* Patient strip */}
      <Box sx={{ px: 4 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="body2"><strong>File #:</strong> {fileNo}</Typography>
          </Grid>
          <Grid item xs={4} textAlign="center">
            <Typography variant="body2"><strong>Client Name:</strong> {clientName}</Typography>
          </Grid>
          <Grid item xs={4} textAlign="right">
            <Typography variant="body2"><strong>DOB:</strong> {dobVal}</Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 3 }} />
      </Box>

      {/* Body */}
      <Box sx={{ px: 4, pb: 4, overflowX: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12pt", tableLayout: "fixed" }}>
          <tbody>
            {/* Billing Information */}
            <Section title="Billing Information" />
            <Row label="Time In" value={fmtTime(timeIn) || "—"} />
            <Row label="Time Out" value={fmtTime(timeOut) || "—"} />
            <Row label="Location" value={location || "—"} />

            {/* Updates */}
            <Section title="Updates" />
            <Row label="New Diagnosis" value={newDiagnosis || "—"} />
            <Row label="New Medication" value={newMedication || "—"} />
            <Row label="Change in Housing/Finances" value={changeInHousingFinances || "—"} />
            <Row label="Overall Health" value={overallHealth || "—"} />
            <Row label="Update from Last Medical Provider Visit" value={updateFromLastVisit || "—"} />

            {/* Breathwork/Grounding */}
            <Section title="Topics Covered During Visit - Breathwork/Grounding" />
            <Row label="Calm Breath for Pregnancy" value={calmBreathPregnancy || "—"} />
            <Row label="Birthing Time Breath" value={birthingTimeBreath || "—"} />
            <Row label="Visualization" value={visualization || "—"} />

            {/* Childbirth Education */}
            <Section title="Childbirth Education" />
            <CheckRow checked={classesCompletedPlanned} label="Classes Completed/Planned" detail={classesCompletedPlannedDetail} />
            <CheckRow checked={questionsAnswered} label="Questions Answered" detail={questionsAnsweredDetail} />
            <CheckRow checked={reviewStagesOfLabor} label="Review Stages of Labor" detail={reviewStagesOfLaborDetail} />
            <CheckRow checked={cervicalExams} label="Cervical Exams" detail={cervicalExamsDetail} />
            <CheckRow checked={acupressurePointsHandout} label="Acupressure Points Handout" detail={acupressurePointsHandoutDetail} />

            {/* Movement */}
            <Section title="Movement for Pregnancy/Labor" />
            <CheckRow checked={reviewActivityLevel} label="Review Current Activity Level and Exercise" detail={reviewActivityLevelDetail} />
            <CheckRow checked={birthBall} label="Birth Ball" detail={birthBallDetail} />
            <CheckRow checked={movementTechniques} label="Movement Techniques (Leg swings, squats, hip circles, etc.)" detail={movementTechniquesDetail} />
            <CheckRow checked={withBirthPartner} label="With Birth Partner (hip squeezes, counterpressure, massage, etc.)" detail={withBirthPartnerDetail} />

            {/* Birth Planning */}
            <Section title="Birth Planning" />
            <CheckRow checked={choiceCardActivity} label="Choice Card Activity" detail={choiceCardActivityDetail} />
            <CheckRow checked={birthLetterDraft} label="Birth Letter/Plan Draft or Completed" detail={birthLetterDraftDetail} />
            <CheckRow checked={cervicalExamsInfo} label="Cervical Exams Information" detail={cervicalExamsInfoDetail} />
            <CheckRow checked={bodyAutonomy} label="Body Autonomy" detail={bodyAutonomyDetail} />
            <CheckRow checked={packingListSnack} label="Packing List/Snack" detail={packingListSnackDetail} />

            {/* Labor Room Environment */}
            <Section title="Labor Room Environment" />
            <CheckRow checked={whoWillAttend} label="Who will attend your birth?" detail={whoWillAttendDetail} />
            <CheckRow checked={doulaExpectations} label="Expectations of your doula" detail={doulaExpectationsDetail} />
            <CheckRow checked={medicalStaffExpectations} label="Expectations of medical staff" detail={medicalStaffExpectationsDetail} />
            <CheckRow checked={partnerExpectations} label="Expectations of your partner" detail={partnerExpectationsDetail} />
            <CheckRow checked={surahsDuringLabor} label="Surahs during labor" detail={surahsDuringLaborDetail} />
            <CheckRow checked={physicalModesty} label="Physical modesty during labor/birth" detail={physicalModestyDetail} />

            {/* Postpartum Planning */}
            <Section title="Postpartum Planning" />
            <CheckRow checked={postpartumPlanningSheet} label="Postpartum Planning Sheet" detail={postpartumPlanningSheetDetail} />
            <CheckRow checked={mainSupportPeople} label="Main support people first month" detail={mainSupportPeopleDetail} />
            <CheckRow checked={safeSleepHandout} label="Safe Sleep Handout" detail={safeSleepHandoutDetail} />
            <CheckRow checked={babyWearing} label="Baby Wearing" detail={babyWearingDetail} />
            <CheckRow checked={returnToActivity} label="Return to Activity" detail={returnToActivityDetail} />
            <CheckRow checked={returnToSex} label="Return to Sex" detail={returnToSexDetail} />

            {/* Lactation */}
            <Section title="Lactation Education/Planning" />
            <CheckRow checked={lactationClasses} label="Classes Completed/Planned" detail={lactationClassesDetail} />
            <CheckRow checked={feedingPlanCompleted} label="Feeding Baby section in PP Plan" detail={feedingPlanCompletedDetail} />
            <Row label="Breast/chest changes noticed" value={breastChanges || "—"} />
            <CheckRow checked={firstFiveDaysPostpartum} label="First 5 days postpartum" detail={firstFiveDaysPostpartumDetail} />
            <CheckRow checked={firstTwoWeeksPostpartum} label="First 2 weeks postpartum" detail={firstTwoWeeksPostpartumDetail} />
            <CheckRow checked={breastPumpsTools} label="Breast pumps/Tools" detail={breastPumpsToolsDetail} />
            <CheckRow checked={alcoholBreastfeeding} label="Alcohol and Breastfeeding" detail={alcoholBreastfeedingDetail} />
            <CheckRow checked={medicationsBreastfeeding} label="Medications and Breastfeeding" detail={medicationsBreastfeedingDetail} />

            {/* Nutrition */}
            <Section title="Nutrition" />
            <Row label="What did you eat yesterday?" value={foodYesterday || "—"} />
            <Row label="Water intake per day" value={waterIntake || "—"} />
            <Row label="Constipated at least one day/week?" value={constipation || "—"} />
            <Row label="Meal prepping/planning" value={mealPrepping || "—"} />

            {/* Mental Health */}
            <Section title="Mental Health" />
            <Row label="History of mental/emotional health struggles?" value={mentalHealthHistory || "—"} />
            <Row label="Struggled with anxiety or depression?" value={anxietyDepression || "—"} />
            <Row label="Easily worrying about things in life?" value={frequentWorrying || "—"} />
            <Row label="Biggest worries about labor and birth?" value={laborBirthWorries || "—"} />

            {/* Referrals */}
            <Section title="Referrals Made During Visit" />
            <Row label="Referrals" value={firstNonEmpty(pn.referralsMade) || "—"} />

            {/* Homework and Next Visit */}
            <Section title="Homework and Next Visit" />
            <Row label="Client Homework" value={firstNonEmpty(pn.clientHomework) || "—"} />
            <Row label="Doula Homework" value={firstNonEmpty(pn.doulaHomework) || "—"} />
            <Row label="Topics for Next Visit" value={firstNonEmpty(pn.topicsNextVisit) || "—"} />

            {/* Signatures */}
            <Section title="Signatures" />
            <Row label="Certified Doula Signature" value={firstNonEmpty(form.messageCertifiedDoulaSignature) || "—"} />
            <Row label="Supervisor Signature" value={firstNonEmpty(form.messageSupervisorSignature) || "—"} />
            <Row label="RN Signature" value={firstNonEmpty(form.messageRnSignature) || "—"} />
            <Row label="NP Signature" value={firstNonEmpty(form.messageNpSignature) || "—"} />
            <Row label="OBGYN Physician Signature" value={firstNonEmpty(form.messageObgynPhysicianSignature) || "—"} />
          </tbody>
        </table>
      </Box>
    </Paper>
  );
};

export default PrenatalVisitPreview;